/* Interest-Free Home Loan Calculator Logic — Math, Charts & CSV Export */

let ifDonutChart = null;
let ifGrowthChart = null;

function getIfInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    loanAmount: getVal('ifLoanAmount', 5000000),
    loanRate: getVal('ifLoanRate', 8.5),
    tenure: getVal('ifTenure', 20),
    sipReturn: getVal('ifSipReturn', 12),
  };
}

function updateIfUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('loanAmount', params.loanAmount);
    url.searchParams.set('loanRate', params.loanRate);
    url.searchParams.set('tenure', params.tenure);
    url.searchParams.set('sipReturn', params.sipReturn);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseIfUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const loanAmount = searchParams.get('loanAmount');
  const loanRate = searchParams.get('loanRate');
  const tenure = searchParams.get('tenure');
  const sipReturn = searchParams.get('sipReturn');

  return {
    loanAmount: loanAmount !== null ? +loanAmount : null,
    loanRate: loanRate !== null ? +loanRate : null,
    tenure: tenure !== null ? +tenure : null,
    sipReturn: sipReturn !== null ? +sipReturn : null,
  };
}

function calculateIf(params) {
  const { loanAmount, loanRate, tenure, sipReturn } = params;

  const n = tenure * 12;
  const rLoan = loanRate / 12 / 100;
  const rSip = sipReturn / 12 / 100;

  // Monthly EMI
  let emi = 0;
  if (rLoan > 0) {
    emi = (loanAmount * rLoan * Math.pow(1 + rLoan, n)) / (Math.pow(1 + rLoan, n) - 1);
  } else {
    emi = loanAmount / n;
  }

  const totalLoanPayment = emi * n;
  const totalInterest = Math.max(0, totalLoanPayment - loanAmount);

  // Required Parallel Monthly SIP to equal total interest
  let requiredSip = 0;
  if (rSip > 0 && n > 0) {
    const factor = ((Math.pow(1 + rSip, n) - 1) / rSip) * (1 + rSip);
    requiredSip = totalInterest / factor;
  } else {
    requiredSip = totalInterest / n;
  }

  const totalSipInvested = requiredSip * n;

  // Schedule computation month by month aggregated yearly
  let balance = loanAmount;
  let cumInterestPaid = 0;
  let sipCorpus = 0;

  const schedule = [];

  for (let m = 1; m <= n; m++) {
    const interestForMonth = balance * rLoan;
    const principalForMonth = emi - interestForMonth;
    balance = Math.max(0, balance - principalForMonth);
    cumInterestPaid += interestForMonth;

    // SIP compounding
    sipCorpus = (sipCorpus + requiredSip) * (1 + rSip);

    if (m % 12 === 0 || m === n) {
      const yearNum = Math.ceil(m / 12);
      schedule.push({
        year: yearNum,
        loanBalance: Math.round(balance),
        cumInterestPaid: Math.round(cumInterestPaid),
        cumSipInvested: Math.round(requiredSip * m),
        sipCorpus: Math.round(sipCorpus),
      });
    }
  }

  return {
    loanAmount: Math.round(loanAmount),
    monthlyEmi: Math.round(emi),
    totalInterest: Math.round(totalInterest),
    requiredSip: Math.round(requiredSip),
    totalSipInvested: Math.round(totalSipInvested),
    finalSipCorpus: Math.round(sipCorpus),
    pctOfEmi: ((requiredSip / emi) * 100).toFixed(1),
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('ifLoanAmountDisplay', fmt(params.loanAmount));
  setTxt('ifLoanRateDisplay', `${params.loanRate}%`);
  setTxt('ifTenureDisplay', `${params.tenure} yrs`);
  setTxt('ifSipReturnDisplay', `${params.sipReturn}%/yr`);
}

function renderIfResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('ifVerdictHeadline', `Parallel Monthly SIP: ${fmt(result.requiredSip)} (${result.pctOfEmi}% of EMI)`);
  setTxt('ifVerdictDetail', `By investing ${fmt(result.requiredSip)}/mo in parallel to your ${fmt(result.monthlyEmi)} EMI, your mutual fund corpus reaches ${fmt(result.finalSipCorpus)}, completely neutralizing your ${fmt(result.totalInterest)} loan interest.`);

  setTxt('ifMonthlyEmiValue', fmt(result.monthlyEmi));
  setTxt('ifParallelSipValue', `${fmt(result.requiredSip)}/mo`);
  setTxt('ifTotalInterestValue', fmt(result.totalInterest));
  setTxt('ifSipCorpusValue', fmt(result.finalSipCorpus));
  setTxt('ifNetInterestValue', `${fmt(0)} (Net Free)`);

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.loanAmount, result.totalInterest, result.totalSipInvested);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('Interest-Free Home Loan Chart rendering error:', err);
  }
}

function renderDonutChart(principal, interest, sipInvested) {
  const ctx = document.getElementById('ifDonutChart');
  if (!ctx) return;

  if (ifDonutChart) ifDonutChart.destroy();

  ifDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Loan Principal Repaid', 'Bank Loan Interest', 'Parallel SIP Capital'],
      datasets: [
        {
          data: [principal, interest, sipInvested],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent'), '#8B5CF6'],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('ifGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const loanBalanceData = schedule.map((r) => r.loanBalance);
  const sipCorpusData = schedule.map((r) => r.sipCorpus);

  if (ifGrowthChart) ifGrowthChart.destroy();

  ifGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Remaining Loan Balance',
          data: loanBalanceData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          borderWidth: 2.5,
        },
        {
          label: 'Parallel SIP Corpus Wealth',
          data: sipCorpusData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('ifScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>${row.year}</td>
      <td>${fmt(row.loanBalance)}</td>
      <td class="advantage-buyer">${fmt(row.cumInterestPaid)}</td>
      <td>${fmt(row.cumSipInvested)}</td>
      <td class="advantage-renter" style="font-weight: 600;">${fmt(row.sipCorpus)}</td>
    </tr>
  `).join('');
}

function exportIfCsv() {
  const params = getIfInputs();
  const result = calculateIf(params);
  const headers = ['Year', 'Loan Principal Remaining', 'Cumulative Interest Paid', 'Cumulative SIP Invested', 'Total SIP Corpus Value'];
  const rows = result.schedule.map((r) => [
    r.year, r.loanBalance, r.cumInterestPaid, r.cumSipInvested, r.sipCorpus
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `interest-free-home-loan-schedule-${params.tenure}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyIfCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultLoan = 5000000;
  let maxLoan = 50000000;
  let stepLoan = 100000;

  if (curr === 'INR') {
    defaultLoan = 5000000;
    maxLoan = 50000000;
    stepLoan = 100000;
  } else if (curr === 'JPY') {
    defaultLoan = 50000000;
    maxLoan = 500000000;
    stepLoan = 1000000;
  }

  const lSlider = document.getElementById('ifLoanAmount');
  if (lSlider) {
    lSlider.max = maxLoan;
    lSlider.step = stepLoan;
    lSlider.value = overrideParams.loanAmount ?? defaultLoan;
  }

  const rSlider = document.getElementById('ifLoanRate');
  if (rSlider && overrideParams.loanRate !== null) {
    rSlider.value = overrideParams.loanRate;
  }

  const tSlider = document.getElementById('ifTenure');
  if (tSlider && overrideParams.tenure !== null) {
    tSlider.value = overrideParams.tenure;
  }

  const sSlider = document.getElementById('ifSipReturn');
  if (sSlider && overrideParams.sipReturn !== null) {
    sSlider.value = overrideParams.sipReturn;
  }
}

function recalculateIf() {
  const params = getIfInputs();
  updateDisplays(params);
  updateIfUrlParams(params);
  const result = calculateIf(params);
  renderIfResults(result, params);
}

function initIfCalculator() {
  if (!document.getElementById('ifLoanAmount')) return;

  const urlParams = parseIfUrlParams();

  initCountry((country) => {
    applyIfCountryRanges(country, urlParams);
    recalculateIf();
  });

  const inputs = ['ifLoanAmount', 'ifLoanRate', 'ifTenure', 'ifSipReturn'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateIf);
      el.addEventListener('change', recalculateIf);
    }
  });

  const exportBtn = document.getElementById('exportIfCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportIfCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initIfCalculator();
});
